import React from "react";

/**
 * TileGrid — responsive grid wrapper for <Tile> rows.
 *
 *   <TileGrid columns={{ base: 1, md: 2, lg: 3 }} gap={3}>
 *     {items.map(i => <Tile key={i.id}>...</Tile>)}
 *   </TileGrid>
 *
 * Notes:
 *   - Column counts must be literals so Tailwind's JIT picks up the classes.
 *   - For a vertical list (default), pass columns={{ base: 1 }} or omit.
 */

type Breakpoint = "base" | "sm" | "md" | "lg" | "xl";
type ColCount = 1 | 2 | 3 | 4 | 5 | 6;

export interface TileGridProps
  extends React.HTMLAttributes<HTMLDivElement> {
  columns?: Partial<Record<Breakpoint, ColCount>>;
  gap?: 2 | 3 | 4 | 6;
  children?: React.ReactNode;
  className?: string;
}

// Static maps so Tailwind sees the literal class names.
const COL_BASE: Record<ColCount, string> = {
  1: "grid-cols-1",
  2: "grid-cols-2",
  3: "grid-cols-3",
  4: "grid-cols-4",
  5: "grid-cols-5",
  6: "grid-cols-6",
};
const COL_SM: Record<ColCount, string> = {
  1: "sm:grid-cols-1",
  2: "sm:grid-cols-2",
  3: "sm:grid-cols-3",
  4: "sm:grid-cols-4",
  5: "sm:grid-cols-5",
  6: "sm:grid-cols-6",
};
const COL_MD: Record<ColCount, string> = {
  1: "md:grid-cols-1",
  2: "md:grid-cols-2",
  3: "md:grid-cols-3",
  4: "md:grid-cols-4",
  5: "md:grid-cols-5",
  6: "md:grid-cols-6",
};
const COL_LG: Record<ColCount, string> = {
  1: "lg:grid-cols-1",
  2: "lg:grid-cols-2",
  3: "lg:grid-cols-3",
  4: "lg:grid-cols-4",
  5: "lg:grid-cols-5",
  6: "lg:grid-cols-6",
};
const COL_XL: Record<ColCount, string> = {
  1: "xl:grid-cols-1",
  2: "xl:grid-cols-2",
  3: "xl:grid-cols-3",
  4: "xl:grid-cols-4",
  5: "xl:grid-cols-5",
  6: "xl:grid-cols-6",
};

const GAP: Record<NonNullable<TileGridProps["gap"]>, string> = {
  2: "gap-2",
  3: "gap-3",
  4: "gap-4",
  6: "gap-6",
};

const TileGrid = React.forwardRef<HTMLDivElement, TileGridProps>(
  function TileGrid(
    { columns, gap = 3, children, className = "", ...rest },
    ref
  ) {
    const cols = columns ?? { base: 1 };
    const colClasses = [
      cols.base ? COL_BASE[cols.base] : COL_BASE[1],
      cols.sm ? COL_SM[cols.sm] : "",
      cols.md ? COL_MD[cols.md] : "",
      cols.lg ? COL_LG[cols.lg] : "",
      cols.xl ? COL_XL[cols.xl] : "",
    ]
      .filter(Boolean)
      .join(" ");

    return (
      <div
        ref={ref}
        className={`grid ${colClasses} ${GAP[gap]} ${className}`}
        {...rest}
      >
        {children}
      </div>
    );
  }
);

export default TileGrid;
